import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
    LayoutDashboard, Users as UsersIcon, MessageSquare, CreditCard,
    UserCog, CalendarClock, LogOut, ShieldCheck, ExternalLink, Gift, Database,
    Activity, Tag,
} from 'lucide-react';

const NAV = [
    { to: '/admin', icon: LayoutDashboard, label: 'Overview', end: true },
    { to: '/admin/users', icon: UsersIcon, label: 'Users' },
    { to: '/admin/questions', icon: MessageSquare, label: 'Questions' },
    { to: '/admin/quality', icon: Activity, label: 'Quality' },
    { to: '/admin/payments', icon: CreditCard, label: 'Payments' },
    { to: '/admin/pricing', icon: Tag, label: 'Pricing' },
    { to: '/admin/experts', icon: UserCog, label: 'Experts' },
    { to: '/admin/consultations', icon: CalendarClock, label: 'Consultations' },
    { to: '/admin/referrals', icon: Gift, label: 'Referrals' },
    { to: '/admin/kp-fixtures', icon: Database, label: 'KP Fixtures' },
];

export default function AdminShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const onLogout = () => { logout(); navigate('/admin/login'); };

    return (
        <div className="relative min-h-screen flex flex-col lg:flex-row bg-obsidian" data-testid="admin-shell">
            <aside className="hidden lg:flex lg:flex-col w-64 border-r border-marigold/15 bg-surface/40 backdrop-blur-sm sticky top-0 h-screen p-6 z-20">
                <div className="mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-marigold/15 border border-marigold/30 flex items-center justify-center">
                            <ShieldCheck size={18} className="text-marigold" />
                        </div>
                        <div>
                            <div className="font-serif text-xl text-parchment leading-none">Admin</div>
                            <div className="overline mt-1">AstroConsult</div>
                        </div>
                    </div>
                </div>
                <nav className="flex flex-col gap-1 flex-1">
                    {NAV.map(({ to, icon: Icon, label, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`admin-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-all ${
                                    isActive
                                        ? 'bg-marigold/15 text-parchment border-l-2 border-marigold'
                                        : 'text-slate-400 hover:bg-elevated hover:text-parchment border-l-2 border-transparent'
                                }`
                            }
                        >
                            <Icon size={16} />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-marigold/10 pt-4 space-y-2 text-sm">
                    <div className="px-2 text-slate-500 truncate" title={user?.email}>{user?.email}</div>
                    <a href="/app" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-2 py-2 text-slate-400 hover:text-parchment">
                        <ExternalLink size={14} /> Open user app
                    </a>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="flex items-center gap-2 px-2 py-2 text-slate-400 hover:text-parchment w-full"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-obsidian/80 backdrop-blur-xl border-b border-marigold/10 flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-marigold" />
                    <span className="font-serif text-lg text-parchment">Admin</span>
                </div>
                <button onClick={onLogout} data-testid="admin-logout-mobile" className="text-slate-400">
                    <LogOut size={16} />
                </button>
            </header>

            <main className="flex-1 relative z-10 pb-24 lg:pb-8">
                {/* Mobile pills */}
                <div className="lg:hidden overflow-x-auto px-4 py-3 border-b border-marigold/10 flex gap-2">
                    {NAV.map(({ to, label, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            className={({ isActive }) =>
                                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${
                                    isActive
                                        ? 'bg-marigold text-obsidian'
                                        : 'bg-elevated text-slate-300 border border-marigold/15'
                                }`
                            }
                        >
                            {label}
                        </NavLink>
                    ))}
                </div>
                <Outlet />
            </main>
        </div>
    );
}
